import React, { createContext, useCallback, useContext, useState } from 'react';

type GoogleIntegrationState = {
    accessToken?: string;
    selfOpenedGooglePicker: boolean;
    originPath: string;
};

type IntegrationContextType = {
    googleIntegration: GoogleIntegrationState;
    setGoogleIntegration: (state: Partial<GoogleIntegrationState>) => void;
};

const IntegrationContext = createContext<IntegrationContextType | undefined>(undefined);

const defaultGoogleIntegration: GoogleIntegrationState = {
    accessToken: localStorage.getItem('googleAccessToken') || undefined,
    selfOpenedGooglePicker: false,
    originPath: localStorage.getItem('googleOriginPath') || '/knowledge',
};

export const IntegrationProvider: React.FC<React.PropsWithChildren<Record<string, unknown>>> = ({ children }) => {
    const [googleIntegration, setGoogleIntegrationState] = useState<GoogleIntegrationState>(defaultGoogleIntegration);

    const setGoogleIntegration = useCallback((state: Partial<GoogleIntegrationState>) => {
        setGoogleIntegrationState((prev) => {
            const newState = { ...prev, ...state };
            if (newState.accessToken) {
                localStorage.setItem('googleAccessToken', newState.accessToken);
            } else {
                localStorage.removeItem('googleAccessToken');
            }
            if (newState.originPath) {
                localStorage.setItem('googleOriginPath', newState.originPath);
            }
            return newState;
        });
    }, []);

    return (
        <IntegrationContext.Provider
            value={{
                googleIntegration,
                setGoogleIntegration,
            }}
        >
            {children}
        </IntegrationContext.Provider>
    );
};

export const useIntegrationContext = () => {
    const context = useContext(IntegrationContext);
    if (!context) {
        throw new Error('useIntegrationContext must be used within an IntegrationProvider.');
    }
    return context;
};
